/**
 * Campaign Manager Dashboard
 * AI-powered marketing campaign management
 * - Facebook Ads (Ready)
 * - Google Ads (Planning)
 * - WhatsApp Campaigns (Tomorrow)
 */
import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import FacebookAdsManager from '../components/FacebookAdsManager';
import WhatsAppBotEditor from '../components/WhatsAppBotEditor';
import CrossPlatformDashboard from '../components/CrossPlatformDashboard';
import ParentOutreach from '../components/ParentOutreach';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared bilingual dictionary for Campaign Manager functional UI.
// NOTE: Facebook/Google/Instagram ad-preview mockups intentionally stay in English
// (they mimic those English social platforms).
const CT = {
  // Header
  cm_title: { en: '📢 AI Campaign Manager', hi: '📢 AI कैंपेन मैनेजर' },
  sub_school: { en: 'Target parents for your school — Facebook, Google, WhatsApp', hi: 'अपने स्कूल के लिए Parents को target करें — Facebook, Google, WhatsApp' },
  sub_platform: { en: 'Target schools — Facebook, Google, WhatsApp', hi: 'Schools को target करें — Facebook, Google, WhatsApp' },
  new_campaign: { en: '➕ New Campaign', hi: '➕ नया कैंपेन' },
  // Platform cards
  ready: { en: '✅ Ready', hi: '✅ तैयार' },
  coming_soon: { en: '🔧 Coming Soon', hi: '🔧 जल्द आ रहा' },
  pc_fb_desc: { en: 'Business account verified. Ready to create campaigns.', hi: 'Business account सत्यापित। कैंपेन बनाने के लिए तैयार।' },
  pc_google_desc: { en: 'API connected. Test Account — Basic Access pending.', hi: 'API जुड़ा। Test Account — Basic Access बाकी।' },
  pc_wa_desc: { en: 'LIVE! Meta WhatsApp Business connected. Templates approved.', hi: 'LIVE! Meta WhatsApp Business जुड़ा। Templates approved।' },
  // Analytics cards
  total_campaigns: { en: 'Total Campaigns', hi: 'कुल कैंपेन' },
  active: { en: 'Active', hi: 'सक्रिय' },
  budget_spent: { en: 'Budget Spent', hi: 'बजट खर्च' },
  hot_leads: { en: '🔥 Hot Leads', hi: '🔥 Hot Leads' },
  total_leads: { en: 'Total Leads', hi: 'कुल Leads' },
  // Tabs
  tab_parent_outreach: { en: 'Parent Outreach', hi: 'अभिभावक आउटरीच' },
  tab_crossplatform: { en: 'Cross-Platform', hi: 'क्रॉस-प्लेटफ़ॉर्म' },
  tab_campaigns: { en: 'Campaigns', hi: 'कैंपेन' },
  tab_live: { en: 'Live Performance', hi: 'लाइव प्रदर्शन' },
  tab_adcopy: { en: 'Ad Copy AI', hi: 'Ad Copy AI' },
  tab_templates: { en: 'Ad Templates', hi: 'Ad टेम्पलेट' },
  tab_fbads: { en: 'Facebook Ads', hi: 'Facebook Ads' },
  tab_whatsappbot: { en: 'WhatsApp Bot', hi: 'WhatsApp बॉट' },
  tab_leads: { en: 'Lead Scoring', hi: 'Lead स्कोरिंग' },
  tab_googleads: { en: 'Google Ads', hi: 'Google Ads' },
  // Campaigns empty
  no_campaigns: { en: 'No Campaigns Yet', hi: 'अभी कोई कैंपेन नहीं' },
  no_campaigns_school: { en: 'Create your first campaign and target parents', hi: 'पहला campaign बनाएं और parents को target करें' },
  no_campaigns_platform: { en: 'Create your first campaign and target schools', hi: 'पहला campaign बनाएं और schools को target करें' },
  create_first: { en: 'Create First Campaign', hi: 'पहला कैंपेन बनाएं' },
  // Campaign card
  impressions: { en: 'Impressions', hi: 'Impressions' },
  clicks: { en: 'Clicks', hi: 'क्लिक्स' },
  leads: { en: 'Leads', hi: 'Leads' },
  spent: { en: 'Spent', hi: 'खर्च' },
  budget: { en: 'Budget', hi: 'बजट' },
  total_word: { en: 'Total', hi: 'कुल' },
  start: { en: '▶ Start', hi: '▶ शुरू' },
  pause: { en: '⏸ Pause', hi: '⏸ रोकें' },
  resume: { en: '▶ Resume', hi: '▶ फिर शुरू' },
  // Live performance
  total_visits: { en: 'Total Visits', hi: 'कुल विज़िट' },
  all_time: { en: 'All time', hi: 'अब तक' },
  today: { en: 'Today', hi: 'आज' },
  since_midnight: { en: 'Since midnight', hi: 'मध्यरात्रि से' },
  sources: { en: 'Sources', hi: 'स्रोत' },
  unique_sources: { en: 'Unique sources', hi: 'अलग स्रोत' },
  active_campaigns: { en: 'Active campaigns', hi: 'सक्रिय कैंपेन' },
  top_feature_urls: { en: 'Top Performing Feature URLs', hi: 'सर्वश्रेष्ठ Feature URLs' },
  refresh: { en: 'Refresh', hi: 'रिफ्रेश' },
  no_visits: { en: 'No visits tracked yet', hi: 'अभी कोई विज़िट ट्रैक नहीं हुई' },
  no_visits_sub: { en: 'Share feature URLs with UTM parameters to start tracking', hi: 'ट्रैकिंग शुरू करने के लिए UTM parameters के साथ feature URLs शेयर करें' },
  example_ad_url: { en: 'Example Ad URL:', hi: 'उदाहरण Ad URL:' },
  copied: { en: 'Copied!', hi: 'कॉपी हुआ!' },
  copy_url: { en: 'Copy URL', hi: 'URL कॉपी' },
  copy_word: { en: 'Copy', hi: 'कॉपी' },
  traffic_by_source: { en: 'Traffic by Source', hi: 'स्रोत अनुसार ट्रैफ़िक' },
  no_source_data: { en: 'No source data yet', hi: 'अभी कोई स्रोत डेटा नहीं' },
  visits_word: { en: 'visits', hi: 'विज़िट' },
  unique_pages: { en: 'unique pages', hi: 'अलग pages' },
  campaign_performance: { en: 'Campaign Performance', hi: 'कैंपेन प्रदर्शन' },
  no_campaign_data: { en: 'No campaign data yet', hi: 'अभी कोई कैंपेन डेटा नहीं' },
  pages_word: { en: 'pages', hi: 'pages' },
  first_word: { en: 'First:', hi: 'पहली:' },
  last_word: { en: 'Last:', hi: 'आख़िरी:' },
  recent_visits: { en: 'Recent Visits', hi: 'हाल की विज़िट' },
  auto_refresh: { en: 'Auto-refreshes every 30s', hi: 'हर 30 सेकंड में auto-refresh' },
  no_recent_visits: { en: 'No recent visits', hi: 'कोई हाल की विज़िट नहीं' },
  c_time: { en: 'Time', hi: 'समय' },
  c_feature: { en: 'Feature', hi: 'फ़ीचर' },
  c_source: { en: 'Source', hi: 'स्रोत' },
  c_campaign: { en: 'Campaign', hi: 'कैंपेन' },
  c_medium: { en: 'Medium', hi: 'माध्यम' },
  quick_url_gen: { en: 'Quick Ad URL Generator', hi: 'त्वरित Ad URL जेनरेटर' },
  quick_url_desc: { en: 'Copy these URLs directly into your Facebook/Google Ad campaigns:', hi: 'इन URLs को सीधे अपने Facebook/Google Ad कैंपेन में कॉपी करें:' },
  // Templates
  campaign_templates: { en: '📋 Campaign Templates', hi: '📋 कैंपेन टेम्पलेट' },
  objective_label: { en: 'Objective:', hi: 'लक्ष्य:' },
  duration: { en: 'Duration:', hi: 'अवधि:' },
  platforms_label: { en: 'Platforms:', hi: 'प्लेटफ़ॉर्म:' },
  days_word: { en: 'days', hi: 'दिन' },
  use_template: { en: 'Use Template', hi: 'टेम्पलेट उपयोग करें' },
  adcopy_templates: { en: '🎨 Ad Copy Templates', hi: '🎨 Ad Copy टेम्पलेट' },
  // Lead scoring
  lead_scoring_system: { en: '🎯 Lead Scoring System', hi: '🎯 Lead स्कोरिंग सिस्टम' },
  hot_leads_70: { en: 'Hot Leads (70+)', hi: 'Hot Leads (70+)' },
  immediate_followup: { en: 'Immediate follow-up', hi: 'तुरंत फ़ॉलो-अप' },
  warm_leads: { en: 'Warm Leads (40-69)', hi: 'Warm Leads (40-69)' },
  schedule_demo: { en: 'Schedule demo', hi: 'Demo शेड्यूल करें' },
  cold_leads: { en: 'Cold Leads (<40)', hi: 'Cold Leads (<40)' },
  nurture_campaign: { en: 'Nurture campaign', hi: 'Nurture कैंपेन' },
  scoring_factors: { en: '📋 Scoring Factors', hi: '📋 स्कोरिंग फ़ैक्टर' },
  engagement: { en: 'Engagement (0-30)', hi: 'Engagement (0-30)' },
  response: { en: 'Response (0-20)', hi: 'Response (0-20)' },
  interest: { en: 'Interest (0-20)', hi: 'Interest (0-20)' },
  school_size: { en: 'School Size (0-30)', hi: 'School Size (0-30)' },
  // Create modal
  create_campaign_title: { en: '➕ Create Campaign', hi: '➕ कैंपेन बनाएं' },
  campaign_name: { en: 'Campaign Name', hi: 'कैंपेन नाम' },
  platform: { en: 'Platform', hi: 'प्लेटफ़ॉर्म' },
  objective: { en: 'Objective', hi: 'लक्ष्य' },
  daily_budget: { en: 'Daily Budget (₹)', hi: 'दैनिक बजट (₹)' },
  total_budget: { en: 'Total Budget (₹)', hi: 'कुल बजट (₹)' },
  cancel: { en: 'Cancel', hi: 'रद्द करें' },
  create_campaign_btn: { en: 'Create Campaign', hi: 'कैंपेन बनाएं' },
  campaign_created: { en: 'Campaign created!', hi: 'कैंपेन बन गया!' },
  // GoogleAdsSection
  connected: { en: 'Connected', hi: 'जुड़ा हुआ' },
  setup_pending: { en: 'Setup Pending', hi: 'सेटअप बाकी' },
  test_acc_pending: { en: 'Test Account Access — Basic Access approval pending from Google', hi: 'Test Account Access — Google से Basic Access approval बाकी' },
  api_configured: { en: 'API credentials configured, awaiting Basic Access', hi: 'API credentials कॉन्फ़िगर, Basic Access की प्रतीक्षा' },
  total_spend: { en: 'Total Spend', hi: 'कुल खर्च' },
  conversions: { en: 'Conversions', hi: 'Conversions' },
  keyword_research: { en: 'Keyword Research (AI-Powered)', hi: 'Keyword Research (AI-आधारित)' },
  search_topic_ph: { en: 'Search topic...', hi: 'विषय खोजें...' },
  search_keywords: { en: 'Search Keywords', hi: 'Keywords खोजें' },
  c_keyword: { en: 'Keyword', hi: 'Keyword' },
  c_avg_cpc: { en: 'Avg CPC', hi: 'औसत CPC' },
  c_competition: { en: 'Competition', hi: 'प्रतिस्पर्धा' },
  c_monthly_searches: { en: 'Monthly Searches', hi: 'मासिक खोज' },
  c_action: { en: 'Action', hi: 'कार्रवाई' },
  add: { en: '+ Add', hi: '+ जोड़ें' },
  google_ads_campaigns: { en: 'Google Ads Campaigns', hi: 'Google Ads कैंपेन' },
  new_google_ad: { en: '+ New Google Ad', hi: '+ नया Google Ad' },
  no_google_ads: { en: 'No Google Ads campaigns yet', hi: 'अभी कोई Google Ads कैंपेन नहीं' },
  no_google_ads_sub: { en: 'Do keyword research and create your first campaign', hi: 'Keyword research करें और पहला campaign बनाएं' },
  api_key_required: { en: 'API Key Required', hi: 'API Key चाहिए' },
  create_google_ad_title: { en: '🔍 Create Google Ad Campaign', hi: '🔍 Google Ad कैंपेन बनाएं' },
  campaign_type: { en: 'Campaign Type', hi: 'कैंपेन प्रकार' },
  ad_headline: { en: 'Ad Headline', hi: 'Ad शीर्षक' },
  ad_description: { en: 'Ad Description', hi: 'Ad विवरण' },
  landing_url: { en: 'Landing URL', hi: 'Landing URL' },
  selected_keywords: { en: 'Selected Keywords', hi: 'चुने गए Keywords' },
  // AdCopyGeneratorTab
  ai_adcopy_gen: { en: 'AI Ad Copy Generator', hi: 'AI Ad Copy जेनरेटर' },
  feature: { en: 'Feature', hi: 'फ़ीचर' },
  ad_platform: { en: 'Ad Platform', hi: 'Ad प्लेटफ़ॉर्म' },
  target_audience: { en: 'Target Audience', hi: 'लक्षित दर्शक' },
  aud_school_owners: { en: 'School Owners / Principals', hi: 'School Owners / प्रधानाचार्य' },
  aud_parents: { en: 'Parents', hi: 'अभिभावक' },
  aud_teachers: { en: 'Teachers', hi: 'शिक्षक' },
  tone: { en: 'Tone', hi: 'लहज़ा' },
  tone_professional: { en: 'Professional', hi: 'पेशेवर' },
  tone_casual: { en: 'Casual / Friendly', hi: 'सहज / मित्रवत' },
  tone_urgent: { en: 'Urgent / Limited Offer', hi: 'तत्काल / सीमित ऑफ़र' },
  tone_emotional: { en: 'Emotional / Story-based', hi: 'भावनात्मक / कहानी-आधारित' },
  ai_generating: { en: 'AI generating...', hi: 'AI बना रहा है...' },
  generate_adcopy: { en: 'Generate Ad Copy', hi: 'Ad Copy बनाएं' },
  generated_adcopy: { en: 'Generated Ad Copy', hi: 'बनाया गया Ad Copy' },
  fields: { en: 'Fields', hi: 'फ़ील्ड्स' },
  preview_as_ad: { en: 'Preview as Ad', hi: 'Ad के रूप में देखें' },
  copy_all: { en: 'Copy All', hi: 'सब कॉपी' },
  landing_url_utm: { en: 'Landing URL with UTM', hi: 'UTM सहित Landing URL' },
  ai_tips: { en: 'AI Tips', hi: 'AI सुझाव' },
  hide_history: { en: 'Hide History', hi: 'इतिहास छिपाएं' },
  show_history: { en: 'Show Generation History', hi: 'जनरेशन इतिहास दिखाएं' },
  generation_failed: { en: 'Generation failed', hi: 'जनरेशन विफल' },
};

// ==================== GOOGLE ADS SECTION ====================
function GoogleAdsSection({ token }) {
  const { language } = useLanguage();
  const tr = (k) => (CT[k] ? CT[k][language] || CT[k].en : k);
  const [keywords, setKeywords] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTopic, setSearchTopic] = useState('school management software');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAd, setNewAd] = useState({
    campaign_name: '', campaign_type: 'SEARCH', daily_budget: 500,
    keywords: [], ad_headline: '', ad_description: '', final_url: 'https://shiksha-mitra.com'
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [campRes, perfRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/campaign/google-ads/campaigns`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/campaign/google-ads/performance?days=30`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/campaign/google-ads/status`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      const campData = await campRes.json();
      const perfData = await perfRes.json();
      const statusData = await statusRes.json();
      if (campData.success) setCampaigns(campData.campaigns);
      if (perfData.success) setPerformance(perfData.overview);
      setConnectionStatus(statusData);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const searchKeywords = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign/google-ads/keyword-suggestions?topic=${encodeURIComponent(searchTopic)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setKeywords(data.keywords);
    } catch (err) { console.error(err); }
  };

  const createCampaign = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign/google-ads/create-campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newAd)
      });
      const data = await res.json();
      if (data.success) {
        setShowCreateForm(false);
        setNewAd({ campaign_name: '', campaign_type: 'SEARCH', daily_budget: 500, keywords: [], ad_headline: '', ad_description: '', final_url: 'https://shiksha-mitra.com' });
        loadAll();
      }
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div></div>;

  return (
    <div className="space-y-6" data-testid="google-ads-section">
      {/* Connection Status */}
      <div className={`rounded-xl border-2 p-5 ${connectionStatus?.connected ? 'border-emerald-200 bg-gradient-to-r from-emerald-50 to-white' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-white'}`} data-testid="google-ads-connection-status">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${connectionStatus?.connected ? 'bg-[#4285F4]' : 'bg-amber-400'}`}>
              <i className="fa-brands fa-google text-white text-xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-800">{connectionStatus?.account_name || 'Google Ads'}</h3>
                {connectionStatus?.connected ? (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-300">
                    <i className="fa-solid fa-circle-check mr-1"></i>{tr('connected')}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full border border-amber-300">
                    <i className="fa-solid fa-clock mr-1"></i>{connectionStatus?.access_level || tr('setup_pending')}
                  </span>
                )}
              </div>
              {connectionStatus?.connected ? (
                <p className="text-sm text-gray-500 mt-0.5">{connectionStatus.customer_id} &middot; {connectionStatus.currency} &middot; {connectionStatus.timezone}</p>
              ) : connectionStatus?.error ? (
                <p className="text-sm text-amber-600 mt-0.5">{tr('test_acc_pending')}</p>
              ) : (
                <p className="text-sm text-gray-500 mt-0.5">{tr('api_configured')}</p>
              )}
            </div>
          </div>
          <button onClick={loadAll} className="p-2 hover:bg-gray-100 rounded-lg transition" data-testid="google-ads-refresh-btn">
            <i className="fa-solid fa-arrows-rotate text-gray-500"></i>
          </button>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: tr('total_spend'), value: `₹${performance?.total_spend || 0}`, color: 'border-red-400' },
          { label: tr('clicks'), value: performance?.total_clicks || 0, color: 'border-blue-400' },
          { label: tr('impressions'), value: performance?.total_impressions || 0, color: 'border-green-400' },
          { label: tr('conversions'), value: performance?.total_conversions || 0, color: 'border-purple-400' },
        ].map((s, i) => (
          <div key={i} className={`bg-white rounded-xl p-4 shadow-sm border-l-4 ${s.color}`}>
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className="text-2xl font-bold text-gray-800">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Keyword Research */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-bold text-gray-700 mb-4">Keyword Research (AI-Powered)</h3>
        <div className="flex gap-2 mb-4">
          <input type="text" value={searchTopic} onChange={(e) => setSearchTopic(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg" placeholder="Search topic..." data-testid="keyword-search-input" />
          <button onClick={searchKeywords} className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600" data-testid="search-keywords-btn">
            Search Keywords
          </button>
        </div>
        {keywords.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{tr('c_keyword')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{tr('c_avg_cpc')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{tr('c_competition')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{tr('c_monthly_searches')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{tr('c_action')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {keywords.map((kw, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{kw.keyword}</td>
                    <td className="px-3 py-2">₹{kw.avg_cpc}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 text-xs rounded ${kw.competition === 'HIGH' ? 'bg-red-100 text-red-700' : kw.competition === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>{kw.competition}</span></td>
                    <td className="px-3 py-2 text-gray-500">{kw.monthly_searches}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => setNewAd(prev => ({...prev, keywords: [...prev.keywords, kw.keyword]}))}
                        className="text-xs text-blue-600 hover:underline">{tr('add')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Campaigns */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-gray-700">{tr('google_ads_campaigns')} ({campaigns.length})</h3>
          <button onClick={() => setShowCreateForm(true)} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm" data-testid="create-google-ad-btn">
            {tr('new_google_ad')}
          </button>
        </div>

        {campaigns.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p className="text-3xl mb-2">🔍</p>
            <p>{tr('no_google_ads')}</p>
            <p className="text-sm mt-1">{tr('no_google_ads_sub')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {campaigns.map(c => (
              <div key={c.campaign_id} className="border rounded-lg p-4 flex justify-between items-center">
                <div>
                  <h4 className="font-medium">{c.campaign_name}</h4>
                  <p className="text-sm text-gray-500">{c.campaign_type} | Budget: ₹{c.daily_budget}/day | Keywords: {c.keywords?.length || 0}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 text-xs rounded-full ${c.status === 'DRAFT' ? 'bg-gray-100 text-gray-600' : c.status === 'PENDING_REVIEW' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                    {c.status}
                  </span>
                  {!c.is_live && <span className="text-xs text-orange-500">{tr('api_key_required')}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Campaign Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">🔍 Create Google Ad Campaign</h3>
              <button onClick={() => setShowCreateForm(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{tr('campaign_name')}</label>
                <input type="text" value={newAd.campaign_name} onChange={(e) => setNewAd({...newAd, campaign_name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" placeholder="School ERP Awareness" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('campaign_type')}</label>
                  <select value={newAd.campaign_type} onChange={(e) => setNewAd({...newAd, campaign_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                    <option value="SEARCH">Search</option>
                    <option value="DISPLAY">Display</option>
                    <option value="VIDEO">Video (YouTube)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('daily_budget')}</label>
                  <input type="number" value={newAd.daily_budget} onChange={(e) => setNewAd({...newAd, daily_budget: Number(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{tr('ad_headline')}</label>
                <input type="text" value={newAd.ad_headline} onChange={(e) => setNewAd({...newAd, ad_headline: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" placeholder="Best School Management Software - Free Demo" maxLength={30} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{tr('ad_description')}</label>
                <textarea value={newAd.ad_description} onChange={(e) => setNewAd({...newAd, ad_description: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" rows={2} placeholder="Complete ERP with Attendance, Exams, Transport..." maxLength={90} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{tr('landing_url')}</label>
                <input type="url" value={newAd.final_url} onChange={(e) => setNewAd({...newAd, final_url: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" />
              </div>
              {newAd.keywords.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('selected_keywords')} ({newAd.keywords.length})</label>
                  <div className="flex flex-wrap gap-1">
                    {newAd.keywords.map((kw, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full flex items-center gap-1">
                        {kw} <button onClick={() => setNewAd({...newAd, keywords: newAd.keywords.filter((_, j) => j !== i)})} className="hover:text-red-500">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowCreateForm(false)} className="flex-1 py-3 border rounded-xl hover:bg-gray-50">{tr('cancel')}</button>
                <button onClick={createCampaign} disabled={!newAd.campaign_name} className="flex-1 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50" data-testid="submit-google-ad-btn">
                  {tr('create_campaign_btn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Ad Preview with Download wrapper
function AdPreviewWithDownload({ platform, copy, featureName, featureSlug, utmUrl }) {
  const previewRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const downloadAsImage = async () => {
    if (!previewRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: '#f9fafb',
        scale: 2,
        useCORS: true,
        logging: false
      });
      const link = document.createElement('a');
      link.download = `${platform}-ad-${featureSlug}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Download error:', err);
    }
    setDownloading(false);
  };

  return (
    <div>
      <div ref={previewRef} className="pb-2">
        <AdPreviewMockup platform={platform} copy={copy} featureName={featureName} featureSlug={featureSlug} utmUrl={utmUrl} />
      </div>
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadAsImage}
          disabled={downloading}
          className="px-5 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition"
          data-testid="download-ad-image-btn"
        >
          {downloading ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Downloading...</>
          ) : (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg> Download as PNG</>
          )}
        </button>
      </div>
    </div>
  );
}

// Ad Preview Mockup Component
function AdPreviewMockup({ platform, copy, featureName, featureSlug, utmUrl }) {
  if (platform === 'facebook') return <FacebookAdPreview copy={copy} featureName={featureName} featureSlug={featureSlug} utmUrl={utmUrl} />;
  if (platform === 'google') return <GoogleAdPreview copy={copy} featureName={featureName} utmUrl={utmUrl} />;
  if (platform === 'instagram') return <InstagramAdPreview copy={copy} featureName={featureName} featureSlug={featureSlug} />;
  return <FacebookAdPreview copy={copy} featureName={featureName} featureSlug={featureSlug} utmUrl={utmUrl} />;
}

// Facebook Ad Preview
function FacebookAdPreview({ copy, featureName, featureSlug, utmUrl }) {
  const FEATURE_COLORS = {
    'homework-hint': 'from-yellow-400 to-orange-500',
    'generate-mcq': 'from-blue-400 to-indigo-500',
    'study-plan': 'from-green-400 to-teal-500',
    'online-fee-payment': 'from-purple-400 to-pink-500',
    'complete-package': 'from-orange-400 to-red-500',
  };
  const gradient = FEATURE_COLORS[featureSlug] || 'from-orange-400 to-amber-500';

  return (
    <div className="max-w-md mx-auto" data-testid="facebook-ad-preview">
      <p className="text-xs text-gray-400 text-center mb-3 uppercase tracking-wider">Facebook Feed Ad Preview</p>
      <div className="bg-white rounded-lg shadow-lg border overflow-hidden">
        {/* Post Header */}
        <div className="p-3 flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-sm">SM</div>
          <div className="flex-1">
            <p className="font-semibold text-sm text-gray-900">Shiksha Mitra</p>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              Sponsored · <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" /></svg>
            </p>
          </div>
          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" /></svg>
        </div>

        {/* Primary Text */}
        <div className="px-3 pb-2">
          <p className="text-sm text-gray-800 leading-relaxed">{copy.primary_text || 'Your ad primary text will appear here...'}</p>
        </div>

        {/* Image Area */}
        <div className={`bg-gradient-to-br ${gradient} h-52 flex items-center justify-center relative`}>
          <div className="text-center text-white px-6">
            <p className="text-2xl font-bold drop-shadow-md">{copy.headline || featureName}</p>
            {copy.headline_2 && <p className="text-sm mt-1 opacity-90">{copy.headline_2}</p>}
          </div>
          <div className="absolute bottom-2 right-2 bg-black/30 px-2 py-0.5 rounded text-white text-xs">
            Ad Creative
          </div>
        </div>

        {/* Link Preview */}
        <div className="px-3 py-2.5 bg-gray-50 border-t border-b">
          <p className="text-xs text-gray-500 uppercase">shiksha-mitra.in</p>
          <p className="text-sm font-semibold text-gray-800 mt-0.5">{copy.headline || featureName}</p>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{copy.description || 'Feature description'}</p>
        </div>

        {/* CTA Button */}
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-4 text-gray-500">
            <button className="flex items-center gap-1 text-sm hover:text-blue-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" /></svg>
              Like
            </button>
            <button className="flex items-center gap-1 text-sm hover:text-blue-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              Comment
            </button>
            <button className="flex items-center gap-1 text-sm hover:text-blue-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
              Share
            </button>
          </div>
          <button className="px-4 py-1.5 bg-[#1877F2] text-white text-sm font-medium rounded-md">
            {copy.cta || 'Learn More'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Google Ad Preview
function GoogleAdPreview({ copy, featureName, utmUrl }) {
  const displayUrl = 'shiksha-mitra.in';
  return (
    <div className="max-w-lg mx-auto" data-testid="google-ad-preview">
      <p className="text-xs text-gray-400 text-center mb-3 uppercase tracking-wider">Google Search Ad Preview</p>
      
      {/* Search Bar */}
      <div className="bg-white rounded-full shadow border flex items-center px-4 py-2.5 mb-4">
        <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        <span className="text-sm text-gray-700">school management software India</span>
      </div>

      {/* Ad Result */}
      <div className="bg-white rounded-lg shadow-lg border p-4">
        {/* Sponsored Badge */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold bg-black text-white px-1.5 py-0.5 rounded">Sponsored</span>
        </div>

        {/* URL */}
        <div className="flex items-center gap-1 mb-1">
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-[8px] font-bold">S</div>
          <div>
            <p className="text-sm text-gray-800">{displayUrl}</p>
            <p className="text-xs text-gray-500">https://{displayUrl}/features/{copy.headline ? '...' : ''}</p>
          </div>
        </div>

        {/* Headlines */}
        <div className="mt-1">
          <a href="#" className="text-xl text-[#1a0dab] hover:underline leading-snug block" onClick={e => e.preventDefault()}>
            {copy.headline || featureName}
            {copy.headline_2 && <span> | {copy.headline_2}</span>}
            {copy.headline_3 && <span> | {copy.headline_3}</span>}
          </a>
        </div>

        {/* Descriptions */}
        <p className="text-sm text-gray-600 mt-1 leading-relaxed">
          {copy.description || copy.primary_text || 'Your ad description will appear here...'}
        </p>

        {/* Sitelinks */}
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1">
          {['AI Features', 'Padhai EMI', 'Free Demo', 'Pricing'].map((link, i) => (
            <a key={i} href="#" className="text-sm text-[#1a0dab] hover:underline" onClick={e => e.preventDefault()}>
              {link}
            </a>
          ))}
        </div>
      </div>

      {/* Organic Results (dimmed) */}
      <div className="mt-3 opacity-40 pointer-events-none">
        <div className="bg-white rounded-lg border p-3">
          <p className="text-xs text-gray-500">www.some-school-software.com</p>
          <p className="text-base text-[#1a0dab]">Another School Management Software</p>
          <p className="text-sm text-gray-500">Generic description of another tool...</p>
        </div>
      </div>
    </div>
  );
}

// Instagram Ad Preview
function InstagramAdPreview({ copy, featureName, featureSlug }) {
  const FEATURE_COLORS = {
    'homework-hint': 'from-yellow-400 to-orange-500',
    'generate-mcq': 'from-blue-400 to-indigo-500',
    'study-plan': 'from-green-400 to-teal-500',
    'online-fee-payment': 'from-purple-400 to-pink-500',
    'complete-package': 'from-orange-400 to-red-500',
  };
  const gradient = FEATURE_COLORS[featureSlug] || 'from-pink-500 to-purple-600';

  return (
    <div className="max-w-sm mx-auto" data-testid="instagram-ad-preview">
      <p className="text-xs text-gray-400 text-center mb-3 uppercase tracking-wider">Instagram Feed Ad Preview</p>
      <div className="bg-white rounded-lg shadow-lg border overflow-hidden">
        {/* Header */}
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-xs ring-2 ring-pink-500 ring-offset-1">SM</div>
            <div>
              <p className="font-semibold text-xs text-gray-900">shiksha_mitra</p>
              <p className="text-[10px] text-gray-500">Sponsored</p>
            </div>
          </div>
          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" /></svg>
        </div>

        {/* Image */}
        <div className={`bg-gradient-to-br ${gradient} aspect-square flex items-center justify-center relative`}>
          <div className="text-center text-white px-8">
            <p className="text-3xl font-extrabold drop-shadow-lg leading-tight">{copy.headline || featureName}</p>
            {copy.headline_2 && <p className="text-base mt-2 opacity-90 font-medium">{copy.headline_2}</p>}
            <div className="mt-4 inline-block bg-white text-gray-800 px-5 py-2 rounded-full text-sm font-bold shadow-lg">
              {copy.cta || 'Learn More'}
            </div>
          </div>
          <div className="absolute bottom-3 left-3 bg-black/40 text-white text-[10px] px-2 py-0.5 rounded">
            shiksha-mitra.in
          </div>
        </div>

        {/* Action Bar */}
        <div className="px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
          </div>
          <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
        </div>

        {/* Caption */}
        <div className="px-3 pb-3">
          <p className="text-sm text-gray-800">
            <span className="font-semibold">shiksha_mitra</span>{' '}
            {copy.primary_text || copy.description || 'Your caption here...'}
          </p>
          {copy.hashtags && copy.hashtags.length > 0 && (
            <p className="text-sm text-[#00376b] mt-1">{copy.hashtags.join(' ')}</p>
          )}
          <button className="mt-2 w-full py-2 bg-blue-500 text-white text-sm font-medium rounded-md">
            {copy.cta || 'Learn More'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Feature options for ad copy
const FEATURE_OPTIONS = [
  { slug: 'homework-hint', name: 'Homework Hints (Comic Mode)' },
  { slug: 'generate-mcq', name: 'MCQ Generator' },
  { slug: 'study-plan', name: 'Study Plan Generator' },
  { slug: 'question-paper-gen', name: 'Question Paper Generator' },
  { slug: 'attendance-alerts', name: 'Attendance Alerts' },
  { slug: 'fee-reminder', name: 'Fee Reminder AI' },
  { slug: 'report-card-gen', name: 'Report Card AI' },
  { slug: 'online-fee-payment', name: 'Online Fee Payment' },
  { slug: 'complete-package', name: 'Complete School Package' },
  { slug: 'chapter-explanation', name: 'Chapter Explainer' },
  { slug: 'practice-tests', name: 'Practice Tests' },
  { slug: 'daily-summary', name: 'Parent Daily Summary' },
  { slug: 'whatsapp-broadcast', name: 'WhatsApp Broadcasting' },
  { slug: 'timetable-generator', name: 'Timetable Generator' },
  { slug: 'analytics-dashboard', name: 'Analytics Dashboard' },
  { slug: 'simplify-notice', name: 'Notice Simplifier' },
  { slug: 'progress-summary', name: 'Student Progress Report' },
  { slug: 'compliance-checklist', name: 'Compliance Checklist' },
  { slug: 'faq', name: 'Doubt Assistant' },
  { slug: 'class-weak-topics', name: 'Weak Topics Identifier' },
  { slug: 'at-risk-alerts', name: 'At-Risk Student Alerts' },
  { slug: 'complaint-draft', name: 'Complaint Reply Generator' },
  { slug: 'leave-management', name: 'Leave Management' },
  { slug: 'transport-management', name: 'Transport Management' },
  { slug: 'emergency-exit', name: 'Emergency Exit System' },
  { slug: 'bulk-import', name: 'Bulk Data Import' },
  { slug: 'udise-portal', name: 'UDISE+ Portal' },
  { slug: 'social-media-manager', name: 'Social Media Manager' },
  { slug: 'school-profile', name: 'School Profile Page' },
  { slug: 'child-progress', name: 'Parent App / Child Progress' },
  { slug: 'ai-wallet', name: 'AI Wallet & Credits' },
  { slug: 'life-skills', name: 'Life Skills Assessment' },
  { slug: 'micro-task', name: 'Micro Task Manager' },
  { slug: 'homework-revision', name: 'Homework Revision Planner' },
  { slug: 'gamified-learning', name: 'Gamified Learning' },
  { slug: 'revision-scheduler', name: 'Revision Scheduler' },
  { slug: 'weekly-challenge', name: 'Weekly Challenge' },
  { slug: 'content-improver', name: 'Content Improver' },
  { slug: 'communication-template', name: 'Communication Templates' },
  { slug: 'student-support', name: 'Student Support AI' },
  { slug: 'workflow-reminder', name: 'Workflow Reminder' },
  { slug: 'format-adapter', name: 'Format Adapter' },
  { slug: 'complaint-guidance', name: 'Complaint Guidance' },
  { slug: 'parent-portal-simplify', name: 'Parent Portal Simplify' },
];

// Ad Copy Generator Tab Component
function AdCopyGeneratorTab({ adCopyForm, setAdCopyForm, generatedCopy, generating, generateAdCopy, copiedField, copyToClipboard, adCopyHistory, loadAdCopyHistory }) {
  const { language } = useLanguage();
  const tr = (k) => (CT[k] ? CT[k][language] || CT[k].en : k);
  const [showHistory, setShowHistory] = useState(false);
  const [activeLang, setActiveLang] = useState('english');
  const [viewMode, setViewMode] = useState('fields');

  return (
    <div className="space-y-6" data-testid="adcopy-generator-section">
      {/* Config Panel */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          {tr('ai_adcopy_gen')}
          <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full ml-2">GPT-5.2</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Feature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tr('feature')}</label>
            <select
              value={adCopyForm.feature_slug}
              onChange={e => {
                const opt = FEATURE_OPTIONS.find(f => f.slug === e.target.value);
                setAdCopyForm({ ...adCopyForm, feature_slug: e.target.value, feature_name: opt?.name || '' });
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="adcopy-feature-select"
            >
              {FEATURE_OPTIONS.map(f => (
                <option key={f.slug} value={f.slug}>{f.name}</option>
              ))}
            </select>
          </div>

          {/* Platform */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tr('ad_platform')}</label>
            <select
              value={adCopyForm.platform}
              onChange={e => setAdCopyForm({ ...adCopyForm, platform: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="adcopy-platform-select"
            >
              <option value="facebook">Facebook / Meta Ads</option>
              <option value="google">Google Ads</option>
              <option value="instagram">Instagram Ads</option>
            </select>
          </div>

          {/* Target Audience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tr('target_audience')}</label>
            <select
              value={adCopyForm.target_audience}
              onChange={e => setAdCopyForm({ ...adCopyForm, target_audience: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="adcopy-audience-select"
            >
              <option value="school_owners">{tr('aud_school_owners')}</option>
              <option value="parents">{tr('aud_parents')}</option>
              <option value="teachers">{tr('aud_teachers')}</option>
            </select>
          </div>

          {/* Tone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tr('tone')}</label>
            <select
              value={adCopyForm.tone}
              onChange={e => setAdCopyForm({ ...adCopyForm, tone: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="adcopy-tone-select"
            >
              <option value="professional">{tr('tone_professional')}</option>
              <option value="casual">{tr('tone_casual')}</option>
              <option value="urgent">{tr('tone_urgent')}</option>
              <option value="emotional">{tr('tone_emotional')}</option>
            </select>
          </div>
        </div>

        <button
          onClick={generateAdCopy}
          disabled={generating}
          className="mt-4 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
          data-testid="generate-adcopy-btn"
        >
          {generating ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              {tr('ai_generating')}
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              {tr('generate_adcopy')}
            </>
          )}
        </button>
      </div>

      {/* Generated Result */}
      {generatedCopy && generatedCopy.ad_copy && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="generated-adcopy-result">
          {/* Header with Language Toggle + View Mode */}
          <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-bold text-gray-800">{tr('generated_adcopy')}</h3>
            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-200 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('fields')}
                  className={`px-3 py-1 text-sm rounded-md transition flex items-center gap-1 ${viewMode === 'fields' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}
                  data-testid="view-fields-btn"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
                  {tr('fields')}
                </button>
                <button
                  onClick={() => setViewMode('preview')}
                  className={`px-3 py-1 text-sm rounded-md transition flex items-center gap-1 ${viewMode === 'preview' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}
                  data-testid="view-preview-btn"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  {tr('preview_as_ad')}
                </button>
              </div>
              {/* Language Toggle */}
              <div className="flex bg-gray-200 rounded-lg p-0.5">
                <button
                  onClick={() => setActiveLang('english')}
                  className={`px-3 py-1 text-sm rounded-md transition ${activeLang === 'english' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}
                  data-testid="lang-english-btn"
                >
                  EN
                </button>
                <button
                  onClick={() => setActiveLang('hindi')}
                  className={`px-3 py-1 text-sm rounded-md transition ${activeLang === 'hindi' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}
                  data-testid="lang-hindi-btn"
                >
                  HI
                </button>
              </div>
            </div>
          </div>

          {/* Fields View */}
          {viewMode === 'fields' && (
          <div className="p-5">
            {(() => {
              const copy = generatedCopy.ad_copy[activeLang] || {};
              return (
                <div className="space-y-4">
                  {copy.headline && (
                    <CopyField label="Headline 1" value={copy.headline} fieldId={`${activeLang}-h1`} copiedField={copiedField} copyToClipboard={copyToClipboard} />
                  )}
                  {copy.headline_2 && (
                    <CopyField label="Headline 2" value={copy.headline_2} fieldId={`${activeLang}-h2`} copiedField={copiedField} copyToClipboard={copyToClipboard} />
                  )}
                  {copy.headline_3 && (
                    <CopyField label="Headline 3" value={copy.headline_3} fieldId={`${activeLang}-h3`} copiedField={copiedField} copyToClipboard={copyToClipboard} />
                  )}
                  {copy.primary_text && (
                    <CopyField label="Primary Text" value={copy.primary_text} fieldId={`${activeLang}-pt`} copiedField={copiedField} copyToClipboard={copyToClipboard} multiline />
                  )}
                  {copy.description && (
                    <CopyField label="Description" value={copy.description} fieldId={`${activeLang}-desc`} copiedField={copiedField} copyToClipboard={copyToClipboard} />
                  )}
                  {copy.cta && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 uppercase tracking-wide w-24 shrink-0">CTA</span>
                      <span className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">{copy.cta}</span>
                    </div>
                  )}
                  {copy.hashtags && copy.hashtags.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wide block mb-2">Hashtags</span>
                      <div className="flex flex-wrap gap-1.5">
                        {copy.hashtags.map((tag, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">{tag}</span>
                        ))}
                        <button
                          onClick={() => copyToClipboard(copy.hashtags.join(' '), `${activeLang}-hashtags`)}
                          className={`px-2 py-1 rounded text-xs transition ${
                            copiedField === `${activeLang}-hashtags` ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                          }`}
                          data-testid="copy-hashtags-btn"
                        >
                          {copiedField === `${activeLang}-hashtags` ? tr('copied') : tr('copy_all')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {generatedCopy.utm_url && (
              <div className="mt-5 pt-4 border-t">
                <span className="text-xs text-gray-500 uppercase tracking-wide block mb-2">{tr('landing_url_utm')}</span>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                  <code className="text-xs text-gray-600 flex-1 break-all">{generatedCopy.utm_url}</code>
                  <button
                    onClick={() => copyToClipboard(generatedCopy.utm_url, 'utm-url')}
                    className={`px-3 py-1.5 rounded text-xs shrink-0 transition ${
                      copiedField === 'utm-url' ? 'bg-green-500 text-white' : 'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                    data-testid="copy-utm-url-btn"
                  >
                    {copiedField === 'utm-url' ? tr('copied') : tr('copy_word')}
                  </button>
                </div>
              </div>
            )}

            {generatedCopy.ad_copy.tips && generatedCopy.ad_copy.tips.length > 0 && (
              <div className="mt-5 pt-4 border-t">
                <span className="text-xs text-gray-500 uppercase tracking-wide block mb-2">{tr('ai_tips')}</span>
                <div className="space-y-1.5">
                  {generatedCopy.ad_copy.tips.map((tip, i) => (
                    <p key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" /></svg>
                      {tip}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Preview as Ad View */}
          {viewMode === 'preview' && (
            <div className="p-5" data-testid="ad-preview-container">
              <AdPreviewWithDownload
                platform={adCopyForm.platform}
                copy={generatedCopy.ad_copy[activeLang] || {}}
                featureName={adCopyForm.feature_name}
                featureSlug={adCopyForm.feature_slug}
                utmUrl={generatedCopy.utm_url}
              />
            </div>
          )}
        </div>
      )}

      {/* History Toggle */}
      <button
        onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadAdCopyHistory(); }}
        className="text-sm text-orange-500 hover:text-orange-600 flex items-center gap-1"
        data-testid="toggle-history-btn"
      >
        <svg className={`w-4 h-4 transition ${showHistory ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        {showHistory ? tr('hide_history') : tr('show_history')}
      </button>

      {/* History */}
      {showHistory && adCopyHistory.length > 0 && (
        <div className="space-y-3" data-testid="adcopy-history">
          {adCopyHistory.map((copy, idx) => (
            <div key={idx} className="bg-white rounded-xl border p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    copy.platform === 'facebook' ? 'bg-blue-100 text-blue-600' :
                    copy.platform === 'google' ? 'bg-red-100 text-red-600' :
                    'bg-pink-100 text-pink-600'
                  }`}>{copy.platform}</span>
                  <span className="font-medium text-sm text-gray-700">{copy.feature_slug}</span>
                  <span className="text-xs text-gray-400">{copy.target_audience} / {copy.tone}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {copy.generated_at ? new Date(copy.generated_at).toLocaleString('hi-IN') : ''}
                </span>
              </div>
              <p className="text-sm text-gray-600 truncate">
                {copy.ad_copy?.english?.headline || copy.ad_copy?.english?.primary_text || '—'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Reusable copy field component
function CopyField({ label, value, fieldId, copiedField, copyToClipboard, multiline }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs text-gray-500 uppercase tracking-wide w-24 shrink-0 pt-2">{label}</span>
      <div className="flex-1 flex items-start gap-2">
        {multiline ? (
          <p className="flex-1 text-gray-800 bg-gray-50 rounded-lg p-3 text-sm leading-relaxed">{value}</p>
        ) : (
          <p className="flex-1 text-gray-800 bg-gray-50 rounded-lg p-3 text-sm font-medium">{value}</p>
        )}
        <button
          onClick={() => copyToClipboard(value, fieldId)}
          className={`px-2 py-2 rounded text-xs shrink-0 transition ${
            copiedField === fieldId ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500 hover:bg-orange-100 hover:text-orange-600'
          }`}
          data-testid={`copy-${fieldId}`}
        >
          {copiedField === fieldId ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
          )}
        </button>
      </div>
    </div>
  );
}

// Platform Status Card
const PlatformCard = ({ platform, name, icon, ready, description, onClick }) => {
  const { language } = useLanguage();
  const tr = (k) => (CT[k] ? CT[k][language] || CT[k].en : k);
  return (
  <div 
    onClick={onClick}
    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
      ready 
        ? 'border-green-300 bg-green-50 hover:shadow-md' 
        : 'border-gray-200 bg-gray-50 hover:border-orange-300'
    }`}
  >
    <div className="flex items-center gap-3 mb-2">
      <span className="text-2xl">{icon}</span>
      <div>
        <h3 className="font-bold">{name}</h3>
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          ready ? 'bg-green-200 text-green-700' : 'bg-yellow-200 text-yellow-700'
        }`}>
          {ready ? tr('ready') : tr('coming_soon')}
        </span>
      </div>
    </div>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
  );
};

// Campaign Card
const CampaignCard = ({ campaign, onStatusChange }) => {
  const { language } = useLanguage();
  const tr = (k) => (CT[k] ? CT[k][language] || CT[k].en : k);
  return (
  <div className="bg-white rounded-xl p-4 border shadow-sm">
    <div className="flex justify-between items-start mb-3">
      <div>
        <h4 className="font-bold">{campaign.name}</h4>
        <p className="text-sm text-gray-500">{campaign.platform.toUpperCase()} • {campaign.objective}</p>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        campaign.status === 'active' ? 'bg-green-100 text-green-700' :
        campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
        campaign.status === 'draft' ? 'bg-gray-100 text-gray-700' :
        'bg-blue-100 text-blue-700'
      }`}>
        {campaign.status}
      </span>
    </div>
    
    <div className="grid grid-cols-4 gap-2 text-center mb-3">
      <div className="bg-gray-50 rounded-lg p-2">
        <p className="text-lg font-bold text-gray-700">{campaign.metrics?.impressions || 0}</p>
        <p className="text-xs text-gray-500">{tr('impressions')}</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-2">
        <p className="text-lg font-bold text-blue-600">{campaign.metrics?.clicks || 0}</p>
        <p className="text-xs text-gray-500">{tr('clicks')}</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-2">
        <p className="text-lg font-bold text-green-600">{campaign.metrics?.leads || 0}</p>
        <p className="text-xs text-gray-500">{tr('leads')}</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-2">
        <p className="text-lg font-bold text-orange-600">₹{campaign.budget_spent || 0}</p>
        <p className="text-xs text-gray-500">{tr('spent')}</p>
      </div>
    </div>
    
    <div className="flex justify-between items-center">
      <div className="text-sm text-gray-500">
        {tr('budget')}: ₹{campaign.budget_daily}/day • {tr('total_word')}: ₹{campaign.budget_total}
      </div>
      <div className="flex gap-2">
        {campaign.status === 'draft' && (
          <button 
            onClick={() => onStatusChange(campaign.campaign_id, 'active')}
            className="px-3 py-1 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
          >
            {tr('start')}
          </button>
        )}
        {campaign.status === 'active' && (
          <button 
            onClick={() => onStatusChange(campaign.campaign_id, 'paused')}
            className="px-3 py-1 bg-yellow-500 text-white rounded-lg text-sm hover:bg-yellow-600"
          >
            {tr('pause')}
          </button>
        )}
        {campaign.status === 'paused' && (
          <button 
            onClick={() => onStatusChange(campaign.campaign_id, 'active')}
            className="px-3 py-1 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
          >
            {tr('resume')}
          </button>
        )}
      </div>
    </div>
  </div>
  );
};

// Lead Score Badge
const LeadScoreBadge = ({ level }) => {
  const colors = {
    hot: 'bg-red-100 text-red-700 border-red-300',
    warm: 'bg-orange-100 text-orange-700 border-orange-300',
    cold: 'bg-blue-100 text-blue-700 border-blue-300'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colors[level] || colors.cold}`}>
      {level === 'hot' ? '🔥 Hot' : level === 'warm' ? '🌡️ Warm' : '❄️ Cold'}
    </span>
  );
};

// Main Component
export default function CampaignManager({ token }) {
  const { language } = useLanguage();
  const tr = (k) => (CT[k] ? CT[k][language] || CT[k].en : k);
  const _role = (() => { try { return (JSON.parse(localStorage.getItem('user') || '{}').role) || ''; } catch { return ''; } })();
  const isSchoolAdmin = _role === 'school_admin';
  const [activeTab, setActiveTab] = useState(isSchoolAdmin ? 'parent-outreach' : 'overview');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [utmStats, setUtmStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [adCopyForm, setAdCopyForm] = useState({
    feature_slug: 'homework-hint',
    feature_name: 'Homework Hints',
    platform: 'facebook',
    target_audience: isSchoolAdmin ? 'parents' : 'school_owners',
    tone: 'professional',
    include_hindi: true
  });
  const [generatedCopy, setGeneratedCopy] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [adCopyHistory, setAdCopyHistory] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  
  // New Campaign Form
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    platform: 'facebook',
    objective: 'lead_generation',
    budget_daily: 500,
    budget_total: 10000
  });

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh UTM stats every 30s when on live tab
  useEffect(() => {
    if (activeTab !== 'live') return;
    const interval = setInterval(loadUtmStats, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const loadUtmStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign/utm-stats/detailed`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setUtmStats(data);
    } catch (err) {
      console.error('Error loading UTM stats:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [campRes, tempRes, anlRes, utmRes] = await Promise.all([
        fetch(`${API_URL}/api/campaign/list`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/campaign/templates`),
        fetch(`${API_URL}/api/campaign/analytics/overview`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/campaign/utm-stats/detailed`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      const campData = await campRes.json();
      if (campData.success) setCampaigns(campData.campaigns);

      const tempData = await tempRes.json();
      if (tempData.success) setTemplates(tempData);

      const anlData = await anlRes.json();
      if (anlData.success) setAnalytics(anlData.analytics);

      const utmData = await utmRes.json();
      if (utmData.success) setUtmStats(utmData);
    } catch (err) {
      console.error('Error loading data:', err);
    }
    setLoading(false);
  };

  const generateAdCopy = async () => {
    setGenerating(true);
    setGeneratedCopy(null);
    try {
      const res = await fetch(`${API_URL}/api/campaign/generate-ad-copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(adCopyForm)
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedCopy(data);
        loadAdCopyHistory();
      } else {
        alert(data.error || tr('generation_failed'));
      }
    } catch (err) {
      console.error('Error generating ad copy:', err);
    }
    setGenerating(false);
  };

  const loadAdCopyHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign/ad-copies/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setAdCopyHistory(data.copies);
    } catch (err) {
      console.error('Error loading ad copy history:', err);
    }
  };

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const createCampaign = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newCampaign,
          target_audience: templates.campaign_templates?.[newCampaign.objective]?.target_audience || {}
        })
      });
      
      const data = await res.json();
      if (data.success) {
        alert(tr('campaign_created'));
        setShowCreateModal(false);
        loadData();
      }
    } catch (err) {
      console.error('Error creating campaign:', err);
    }
  };

  const updateCampaignStatus = async (campaignId, status) => {
    try {
      await fetch(`${API_URL}/api/campaign/${campaignId}/status?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="campaign-manager">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            {tr('cm_title')}
          </h1>
          <p className="text-gray-500">{isSchoolAdmin ? tr('sub_school') : tr('sub_platform')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
        >
          {tr('new_campaign')}
        </button>
      </div>

      {/* Platform Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PlatformCard
          platform="facebook"
          name="Facebook Ads"
          icon="📘"
          ready={true}
          description={tr('pc_fb_desc')}
          onClick={() => setSelectedPlatform('facebook')}
        />
        <PlatformCard
          platform="google"
          name="Google Ads"
          icon="🔍"
          ready={false}
          description={tr('pc_google_desc')}
          onClick={() => setSelectedPlatform('google')}
        />
        <PlatformCard
          platform="whatsapp"
          name="WhatsApp Campaigns"
          icon="💬"
          ready={true}
          description={tr('pc_wa_desc')}
          onClick={() => setSelectedPlatform('whatsapp')}
        />
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">{tr('total_campaigns')}</p>
            <p className="text-3xl font-bold">{analytics.campaigns?.total || 0}</p>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">{tr('active')}</p>
            <p className="text-3xl font-bold">{analytics.campaigns?.active || 0}</p>
          </div>
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">{tr('budget_spent')}</p>
            <p className="text-3xl font-bold">₹{analytics.budget?.total_spent || 0}</p>
          </div>
          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">{tr('hot_leads')}</p>
            <p className="text-3xl font-bold">{analytics.leads?.hot || 0}</p>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">{tr('total_leads')}</p>
            <p className="text-3xl font-bold">{analytics.leads?.total || 0}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        {[
          ...(isSchoolAdmin ? [{ id: 'parent-outreach', label: tr('tab_parent_outreach'), icon: '📲' }] : []),
          { id: 'crossplatform', label: tr('tab_crossplatform'), icon: '📊' },
          { id: 'overview', label: tr('tab_campaigns'), icon: '📊' },
          { id: 'live', label: tr('tab_live'), icon: '📡' },
          { id: 'adcopy', label: tr('tab_adcopy'), icon: '✍️' },
          ...(isSchoolAdmin ? [] : [{ id: 'templates', label: tr('tab_templates'), icon: '🎨' }]),
          { id: 'fbads', label: tr('tab_fbads'), icon: '📘' },
          ...(isSchoolAdmin ? [] : [{ id: 'whatsappbot', label: tr('tab_whatsappbot'), icon: '💬' }]),
          { id: 'leads', label: tr('tab_leads'), icon: '🎯' },
          { id: 'googleads', label: tr('tab_googleads'), icon: '🔍' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); if (tab.id === 'live') loadUtmStats(); }}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'text-orange-500 border-b-2 border-orange-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Parent Outreach Tab (school admin) */}
      {activeTab === 'parent-outreach' && (
        <ParentOutreach token={token} />
      )}

      {/* Cross-Platform Dashboard Tab */}
      {activeTab === 'crossplatform' && (
        <CrossPlatformDashboard token={token} />
      )}

      {/* Campaigns Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {campaigns.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <span className="text-4xl">📢</span>
              <h3 className="font-bold text-gray-700 mt-4">{tr('no_campaigns')}</h3>
              <p className="text-gray-500 mt-2">{isSchoolAdmin ? tr('no_campaigns_school') : tr('no_campaigns_platform')}</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg"
              >
                {tr('create_first')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {campaigns.map(campaign => (
                <CampaignCard
                  key={campaign.campaign_id}
                  campaign={campaign}
                  onStatusChange={updateCampaignStatus}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Live Ad Performance Tab */}
      {activeTab === 'live' && (
        <div className="space-y-6" data-testid="live-performance-section">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{tr('total_visits')}</p>
              <p className="text-3xl font-bold text-gray-800 mt-1" data-testid="total-visits">{utmStats?.total_visits || 0}</p>
              <p className="text-xs text-gray-400 mt-1">{tr('all_time')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{tr('today')}</p>
              <p className="text-3xl font-bold text-green-600 mt-1" data-testid="today-visits">{utmStats?.today_visits || 0}</p>
              <p className="text-xs text-gray-400 mt-1">{tr('since_midnight')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{tr('sources')}</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{utmStats?.by_source?.length || 0}</p>
              <p className="text-xs text-gray-400 mt-1">{tr('unique_sources')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{tr('tab_campaigns')}</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{utmStats?.by_campaign?.length || 0}</p>
              <p className="text-xs text-gray-400 mt-1">{tr('active_campaigns')}</p>
            </div>
          </div>

          {/* Top Feature URLs */}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                {tr('top_feature_urls')}
              </h3>
              <button onClick={loadUtmStats} className="text-sm text-orange-500 hover:text-orange-600 flex items-center gap-1" data-testid="refresh-stats-btn">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                {tr('refresh')}
              </button>
            </div>
            {(!utmStats?.top_features || utmStats.top_features.length === 0) ? (
              <div className="p-8 text-center">
                <span className="text-4xl block mb-3">📡</span>
                <h4 className="font-bold text-gray-600">{tr('no_visits')}</h4>
                <p className="text-sm text-gray-400 mt-1">{tr('no_visits_sub')}</p>
                <div className="mt-4 bg-gray-50 rounded-lg p-3 text-xs text-left max-w-md mx-auto">
                  <p className="text-gray-500 mb-1">{tr('example_ad_url')}</p>
                  <code className="text-orange-600 break-all">{window.location.origin}/features/homework-hint?utm_source=facebook&utm_campaign=summer2025</code>
                </div>
              </div>
            ) : (
              <div className="divide-y">
                {utmStats.top_features.map((f, idx) => {
                  const barWidth = utmStats.top_features[0]?.visits ? Math.max(8, (f.visits / utmStats.top_features[0].visits) * 100) : 0;
                  return (
                    <div key={idx} className="p-4 hover:bg-gray-50 transition" data-testid={`feature-row-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-lg font-bold text-gray-300 w-6 text-right shrink-0">#{idx + 1}</span>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-800 truncate">{f.feature_slug || 'Homepage'}</p>
                            <p className="text-xs text-gray-400 truncate">{f.url}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-lg font-bold text-orange-600">{f.visits}</span>
                          <button
                            onClick={() => {
                              const adUrl = `${window.location.origin}/features/${f.feature_slug}?utm_source=facebook&utm_campaign=YOUR_CAMPAIGN`;
                              navigator.clipboard.writeText(adUrl);
                              setCopiedUrl(f.feature_slug);
                              setTimeout(() => setCopiedUrl(null), 2000);
                            }}
                            className={`px-2 py-1 rounded text-xs transition ${
                              copiedUrl === f.feature_slug 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-orange-600'
                            }`}
                            data-testid={`copy-url-${idx}`}
                          >
                            {copiedUrl === f.feature_slug ? tr('copied') : tr('copy_url')}
                          </button>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="ml-9">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-orange-400 to-amber-500 rounded-full transition-all" style={{ width: `${barWidth}%` }}></div>
                        </div>
                        <div className="flex gap-2 mt-1.5">
                          {f.sources?.map((src, si) => (
                            <span key={si} className={`text-xs px-1.5 py-0.5 rounded ${
                              src === 'facebook' ? 'bg-blue-50 text-blue-600' :
                              src === 'google' ? 'bg-red-50 text-red-600' :
                              src === 'instagram' ? 'bg-pink-50 text-pink-600' :
                              'bg-gray-50 text-gray-600'
                            }`}>{src}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Source Distribution + Campaign Performance side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source Distribution */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-bold text-gray-800">{tr('traffic_by_source')}</h3>
              </div>
              {(!utmStats?.by_source || utmStats.by_source.length === 0) ? (
                <div className="p-6 text-center text-gray-400 text-sm">{tr('no_source_data')}</div>
              ) : (
                <div className="p-4 space-y-3">
                  {utmStats.by_source.map((s, idx) => {
                    const maxVisits = utmStats.by_source[0]?.visits || 1;
                    const pct = Math.round((s.visits / (utmStats.total_visits || 1)) * 100);
                    const sourceColors = {
                      facebook: { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50' },
                      google: { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-50' },
                      instagram: { bg: 'bg-pink-500', text: 'text-pink-600', light: 'bg-pink-50' },
                      whatsapp: { bg: 'bg-green-500', text: 'text-green-600', light: 'bg-green-50' },
                    };
                    const color = sourceColors[s.source] || { bg: 'bg-gray-500', text: 'text-gray-600', light: 'bg-gray-50' };
                    return (
                      <div key={idx} data-testid={`source-${s.source}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-sm font-medium ${color.text}`}>{s.source}</span>
                          <span className="text-sm text-gray-500">{s.visits} {tr('visits_word')} ({pct}%)</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full ${color.bg} rounded-full transition-all`} style={{ width: `${Math.max(5, (s.visits / maxVisits) * 100)}%` }}></div>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{s.unique_pages} {tr('unique_pages')}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Campaign Performance */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-bold text-gray-800">{tr('campaign_performance')}</h3>
              </div>
              {(!utmStats?.by_campaign || utmStats.by_campaign.length === 0) ? (
                <div className="p-6 text-center text-gray-400 text-sm">{tr('no_campaign_data')}</div>
              ) : (
                <div className="divide-y">
                  {utmStats.by_campaign.map((c, idx) => (
                    <div key={idx} className="p-4" data-testid={`campaign-row-${idx}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-800">{c.campaign}</p>
                          <p className="text-xs text-gray-400">{c.source} • {c.unique_pages} {tr('pages_word')}</p>
                        </div>
                        <span className="text-lg font-bold text-orange-600">{c.visits}</span>
                      </div>
                      <div className="flex gap-4 mt-2 text-xs text-gray-400">
                        <span>{tr('first_word')} {c.first_visit ? new Date(c.first_visit).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN') : '-'}</span>
                        <span>{tr('last_word')} {c.last_visit ? new Date(c.last_visit).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN') : '-'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Visits */}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">{tr('recent_visits')}</h3>
              <span className="text-xs text-gray-400">{tr('auto_refresh')}</span>
            </div>
            {(!utmStats?.recent_visits || utmStats.recent_visits.length === 0) ? (
              <div className="p-6 text-center text-gray-400 text-sm">{tr('no_recent_visits')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left">{tr('c_time')}</th>
                      <th className="px-4 py-2 text-left">{tr('c_feature')}</th>
                      <th className="px-4 py-2 text-left">{tr('c_source')}</th>
                      <th className="px-4 py-2 text-left">{tr('c_campaign')}</th>
                      <th className="px-4 py-2 text-left">{tr('c_medium')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {utmStats.recent_visits.map((v, idx) => {
                      const feature = v.landing_url?.includes('/features/') 
                        ? v.landing_url.split('/features/')[1]?.split('?')[0] 
                        : 'Homepage';
                      return (
                        <tr key={idx} className="hover:bg-gray-50" data-testid={`visit-row-${idx}`}>
                          <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                            {v.visited_at ? new Date(v.visited_at).toLocaleString(language === 'hi' ? 'hi-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : '-'}
                          </td>
                          <td className="px-4 py-2 font-medium text-gray-700">{feature}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              v.utm_source === 'facebook' ? 'bg-blue-50 text-blue-600' :
                              v.utm_source === 'google' ? 'bg-red-50 text-red-600' :
                              'bg-gray-50 text-gray-600'
                            }`}>{v.utm_source || '-'}</span>
                          </td>
                          <td className="px-4 py-2 text-gray-600">{v.utm_campaign || '-'}</td>
                          <td className="px-4 py-2 text-gray-400">{v.utm_medium || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* URL Generator */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-5">
            <h3 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              Quick Ad URL Generator
            </h3>
            <p className="text-sm text-orange-700 mb-3">{tr('quick_url_desc')}</p>
            <div className="space-y-2">
              {['homework-hint', 'generate-mcq', 'study-plan', 'online-fee-payment', 'complete-package'].map(slug => {
                const adUrl = `${window.location.origin}/features/${slug}?utm_source=facebook&utm_medium=cpc&utm_campaign=YOUR_CAMPAIGN`;
                return (
                  <div key={slug} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-orange-100">
                    <code className="flex-1 text-xs text-gray-600 truncate">{adUrl}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(adUrl);
                        setCopiedUrl(`gen-${slug}`);
                        setTimeout(() => setCopiedUrl(null), 2000);
                      }}
                      className={`px-3 py-1 rounded text-xs shrink-0 transition ${
                        copiedUrl === `gen-${slug}` ? 'bg-green-500 text-white' : 'bg-orange-500 text-white hover:bg-orange-600'
                      }`}
                      data-testid={`gen-copy-${slug}`}
                    >
                      {copiedUrl === `gen-${slug}` ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Ad Copy AI Tab */}
      {activeTab === 'adcopy' && (
        <AdCopyGeneratorTab
          adCopyForm={adCopyForm}
          setAdCopyForm={setAdCopyForm}
          generatedCopy={generatedCopy}
          generating={generating}
          generateAdCopy={generateAdCopy}
          copiedField={copiedField}
          copyToClipboard={copyToClipboard}
          adCopyHistory={adCopyHistory}
          loadAdCopyHistory={loadAdCopyHistory}
        />
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          {/* Campaign Templates */}
          <div>
            <h3 className="font-bold text-lg mb-4">{tr('campaign_templates')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(templates.campaign_templates || {}).map(([key, template]) => (
                <div key={key} className="bg-white rounded-xl p-4 border shadow-sm">
                  <h4 className="font-bold">{template.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{tr('objective_label')} {template.objective}</p>
                  <div className="mt-3 space-y-1 text-sm">
                    <p>💰 {tr('budget')}: ₹{template.suggested_budget_daily}/day</p>
                    <p>📅 {tr('duration')} {template.suggested_duration_days} {tr('days_word')}</p>
                    <p>📱 {tr('platforms_label')} {template.best_platforms?.join(', ')}</p>
                  </div>
                  <button
                    onClick={() => {
                      setNewCampaign({
                        ...newCampaign,
                        name: template.name,
                        objective: template.objective,
                        budget_daily: template.suggested_budget_daily,
                        budget_total: template.suggested_budget_daily * template.suggested_duration_days
                      });
                      setShowCreateModal(true);
                    }}
                    className="mt-3 w-full py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200"
                  >
                    {tr('use_template')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Ad Copy Templates */}
          <div>
            <h3 className="font-bold text-lg mb-4">{tr('adcopy_templates')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(templates.ad_copy_templates?.facebook || {}).map(([objective, copy]) => (
                <div key={objective} className="bg-white rounded-xl p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">📘</span>
                    <span className="font-bold">Facebook - {objective}</span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <p className="font-semibold">{copy.headline}</p>
                    <p className="text-sm text-gray-600">{copy.description}</p>
                    <span className="inline-block px-3 py-1 bg-blue-500 text-white text-sm rounded-full">
                      {copy.cta}
                    </span>
                  </div>
                  <div className="mt-2 bg-orange-50 rounded-lg p-3 space-y-2">
                    <p className="font-semibold">{copy.headline_hi}</p>
                    <p className="text-sm text-gray-600">{copy.description_hi}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Facebook Ads Tab */}
      {activeTab === 'fbads' && (
        <FacebookAdsManager token={token} />
      )}

      {activeTab === 'whatsappbot' && (
        <WhatsAppBotEditor token={token} />
      )}

      {/* Lead Scoring Tab */}
      {activeTab === 'leads' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-6 border">
            <h3 className="font-bold text-lg mb-4">{tr('lead_scoring_system')}</h3>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-red-50 rounded-xl p-4 text-center border border-red-200">
                <span className="text-3xl">🔥</span>
                <p className="text-2xl font-bold text-red-600 mt-2">{analytics?.leads?.hot || 0}</p>
                <p className="text-sm text-red-600">{tr('hot_leads_70')}</p>
                <p className="text-xs text-gray-500 mt-1">{tr('immediate_followup')}</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-200">
                <span className="text-3xl">🌡️</span>
                <p className="text-2xl font-bold text-orange-600 mt-2">{analytics?.leads?.warm || 0}</p>
                <p className="text-sm text-orange-600">{tr('warm_leads')}</p>
                <p className="text-xs text-gray-500 mt-1">{tr('schedule_demo')}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-200">
                <span className="text-3xl">❄️</span>
                <p className="text-2xl font-bold text-blue-600 mt-2">{analytics?.leads?.cold || 0}</p>
                <p className="text-sm text-blue-600">{tr('cold_leads')}</p>
                <p className="text-xs text-gray-500 mt-1">{tr('nurture_campaign')}</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold mb-3">{tr('scoring_factors')}</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-gray-700">{tr('engagement')}</p>
                  <ul className="text-gray-500 ml-4 list-disc">
                    <li>Video watched: +10</li>
                    <li>Form filled: +15</li>
                    <li>Demo attended: +30</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">{tr('response')}</p>
                  <ul className="text-gray-500 ml-4 list-disc">
                    <li>Quick response (&lt;1hr): +20</li>
                    <li>Good response (&lt;24hr): +10</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">{tr('interest')}</p>
                  <ul className="text-gray-500 ml-4 list-disc">
                    <li>Asked pricing: +15</li>
                    <li>Callback request: +10</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">{tr('school_size')}</p>
                  <ul className="text-gray-500 ml-4 list-disc">
                    <li>1000+ students: +30</li>
                    <li>500-1000: +20</li>
                    <li>100-500: +10</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Ads Tab */}
      {activeTab === 'googleads' && (
        <GoogleAdsSection token={token} />
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">{tr('create_campaign_title')}</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{tr('campaign_name')}</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="School Awareness Q1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('platform')}</label>
                  <select
                    value={newCampaign.platform}
                    onChange={(e) => setNewCampaign({...newCampaign, platform: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="facebook">📘 Facebook</option>
                    <option value="google">Google Ads</option>
                    <option value="whatsapp">💬 WhatsApp (Soon)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('objective')}</label>
                  <select
                    value={newCampaign.objective}
                    onChange={(e) => setNewCampaign({...newCampaign, objective: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="awareness">🎯 Awareness</option>
                    <option value="lead_generation">📋 Lead Generation</option>
                    <option value="demo_booking">📞 Demo Booking</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('daily_budget')}</label>
                  <input
                    type="number"
                    value={newCampaign.budget_daily}
                    onChange={(e) => setNewCampaign({...newCampaign, budget_daily: Number(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('total_budget')}</label>
                  <input
                    type="number"
                    value={newCampaign.budget_total}
                    onChange={(e) => setNewCampaign({...newCampaign, budget_total: Number(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 border rounded-xl hover:bg-gray-50"
                >
                  {tr('cancel')}
                </button>
                <button
                  onClick={createCampaign}
                  disabled={!newCampaign.name}
                  className="flex-1 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50"
                >
                  {tr('create_campaign_btn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
